import { createContext, ReactNode, useContext, useMemo } from "react";

import { useFeaturesQuery } from "~/api/account";
import { type AvailableFeature, type Feature } from "~/api/graphql-types";

export interface IFeaturesContext {
  availableFeatures: Pick<AvailableFeature, "permissionName">[];
  editableFeatures: Pick<Feature, "permissionName" | "enabled" | "id" | "name" | "description">[];
  featuresLoaded: boolean;
  featureEnabled: (key: FeatureFlag) => boolean;
  refetchFeatures: () => void;
}

const FeatureContext = createContext<IFeaturesContext>({
  availableFeatures: [],
  editableFeatures: [],
  featuresLoaded: true,
  featureEnabled: () => false,
  refetchFeatures: () => null,
});

export const useFeatures = () => useContext(FeatureContext);

export const FeatureState = ({ children }: { children: ReactNode }) => {
  const { data, refetch: refetchFeatures } = useFeaturesQuery();

  const availableFeatures = useMemo(() => data?.availableFeatures?.features ?? [], [data]);
  const editableFeatures = useMemo(() => data?.editableFeatures?.features ?? [], [data]);

  const value = useMemo(
    () => ({
      availableFeatures,
      editableFeatures,
      featuresLoaded: !!data,
      featureEnabled: (key: string) => !!availableFeatures.find(f => f.permissionName === key),
      refetchFeatures,
    }),
    [availableFeatures, data, refetchFeatures]
  );

  return <FeatureContext.Provider value={value}>{children}</FeatureContext.Provider>;
};

export const FeatureFlag = {
  AccBilling: "ACC_BILLING",
  AccPurchaseOrders: "ACC_PURCHASE_ORDERS",
  AuditTrail: "AUDIT_TRAIL",
  BookingSystem: "BOOKING_SYSTEM",
  Extracts: "EXTRACTS",
  Groups: "GROUPS",
  GroupsExperimental: "GROUPS_EXPERIMENTAL",
  HealthOne: "HEALTHONE",
  Outcomes: "OUTCOMES",
  Primhd: "PRIMHD",
  RelationshipManagement: "RELATIONSHIP_MANAGEMENT",
  Reports: "REPORTS",
  SmsNotifications: "SMS_NOTIFICATIONS",
  TeamAccessControls: "TEAM_ACCESS_CONTROLS",
  TeamAdministration: "TEAM_ADMINISTRATION",
  Whanau: "WHANAU",
  WhanaungaAwhina: "WHANAUNGA_AWHINA",
  WhanaungaAwhinaDocuments: "WHANAUNGA_AWHINA_DOCUMENTS",
  UserAccessControl: "USER_ACCESS_CONTROL",
} as const;
export type FeatureFlag = (typeof FeatureFlag)[keyof typeof FeatureFlag];
